import PartyPopperIcon from '@/assets/icons/party_popper.png';
import {
  Button,
  Card,
  CustomerRoleTypeBadge,
  HSpacer,
  InfoTooltip,
  Text,
  TextBadge,
  TextLink,
  VSpacer,
} from '@/components/DesignSystem';
import { ProgramExpirationBanner } from '@/components/shared/ProgramExpirationBanner';
import { Routes } from '@/constants/Routes';
import { useDashboard } from '@/hooks/useDashboard';
import {
  buttonHoverStyle,
  cardStyle,
  DashboardDisclaimer,
  DashboardFooter,
  DashboardHeader,
  DashboardProgramInfo,
  DashboardViewButton,
  darkenedSectionStyle,
  FarmerGreeting as DashboardGreeting,
  getBrandColor,
  kpiCardWrapperStyle,
  primaryKpiCardWrapperStyle,
} from '@/pages/CustomerDashboard/DashboardLayout';
import { TierBenefitsModal } from '@/pages/CustomerDashboard/Benefits/TierBenefitsModal';
import { CustomerRoleBadge } from '@/pages/CustomerDashboard/CustomerRoleBadge';
import { ProgramSwitch } from '@/pages/CustomerDashboard/Energy/ProgramSwitch';
import { useDashboardProgram } from '@/pages/CustomerDashboard/Energy/useDashboardProgram';
import { DashboardData, ResolvedPromotion } from '@/pages/CustomerDashboard/interfaces';
import { LinkedAccountsModal } from '@/pages/CustomerDashboard/LinkedAccountsModal';
import { MyProgressModal } from '@/pages/CustomerDashboard/MyProgress/MyProgressModal';
import { NewProgramWelcomeModal } from '@/pages/CustomerDashboard/NewProgramWelcomeModal';
import { PointsExpirationBanner } from "@/pages/CustomerDashboard/PointsExpirationBanner";
import { ProgramExclusionsDisclaimer } from '@/pages/CustomerDashboard/ProgramExclusionsDisclaimer';
import {
  ProgramPointsExpirationModal,
} from '@/pages/CustomerDashboard/ProgramPointsExpirationModal';
import { PromotionCard } from '@/pages/CustomerDashboard/Promotions/PromotionCard';
import { PromotionDetailsModal } from '@/pages/CustomerDashboard/Promotions/PromotionDetailsModal';
import { ReEarnStatusBanner } from '@/pages/CustomerDashboard/ReEarnStatusBanner';
import { TierImage } from '@/pages/CustomerDashboard/TierImage';
import { TierProgressBar } from '@/pages/CustomerDashboard/TierProgressBar';
import { TotalSpendProgressBar } from '@/pages/CustomerDashboard/TotalSpendProgressBar';
import { KPICard } from '@/pages/RetailerOverview/PointsOverview/KPICard';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { buildRequirementTextForTier } from "@/utilities/RewardsProgramUtility";
import { ApiRetailer, ApiUser } from '@api/interfaces';
import { ApiFarmerRewards } from '@api/interfaces/ApiFarmerRewards';
import { ApiRewardsProgram } from "@api/interfaces/ApiRewardsProgram";
import ChevronRight from '@mui/icons-material/ChevronRight';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import Check from '@mui/icons-material/Check';
import {
  alpha,
  Box,
  ButtonBase,
  CircularProgress,
  Container,
  Divider,
  Stack,
  useTheme,
} from '@mui/material';
import { CIBPageName } from '@shared/enums';
import {
  calculateAvailablePoints,
  calculateLockedPoints,
  calculateReservedPoints,
  formatNumber,
  formatShortCurrency,
  getJsDateFromString,
} from '@shared/utilities';
import type { TFunction } from 'i18next';
import i18next from 'i18next';
import { sortBy } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { getProgramInfo, useDashboardNavigate } from './helpers';
import { AboutAccountRolesBanner } from './LinkedAccounts';

export const defaultDashboardData: DashboardData = {
  balances: [],
  farmer: {} as ApiUser,
  linkedAccounts: [],
  manufacturers: [],
  orders: [],
  promotions: [],
  retailer: {} as ApiRetailer,
};

export const buildProgramProgressText = (
  farmerRewards: ApiFarmerRewards | null,
  program: ApiRewardsProgram,
  t: TFunction,
) => {
  const programStartYear = getJsDateFromString(program.startDate).getFullYear();
  const programEndYear = getJsDateFromString(program.endDate).getFullYear();

  const yearDiff = programEndYear - programStartYear;
  const yearText = yearDiff === 0
    ? programEndYear + 1 : `${programStartYear + yearDiff}-${programEndYear + yearDiff}`;

  const hasRankOverride = farmerRewards && farmerRewards.rankOverride !== null;
  if (hasRankOverride) {
    return t(
      ($) => $.customerDashboard.programProgress.reEarning,
      { yearText },
    );
  }
  return t(($) => $.customerDashboard.programProgress.earning, { yearText });
};

export const CustomerDashboard = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { openSnackbar } = useSnackbar();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken') ?? params.accessToken;
  const navigateToPage = useDashboardNavigate();
  const [showPromotionDetailsModal, setShowPromotionDetailsModal] = useState(false);
  const [showTierBenefitsModal, setShowTierBenefitsModal] = useState(false);
  const [showLinkedAccountsModal, setShowLinkedAccountsModal] = useState(false);
  const [showMyProgressModal, setShowMyProgressModal] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState<ResolvedPromotion | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const { data: dashboardData } = useDashboard(accessToken);
  const { excludeEnergyBalances, memberships } = useDashboardProgram(accessToken);

  const {
    activeFarmerRewards,
    activeProgram,
    balances,
    categories,
    farmer,
    linkedAccounts,
    manufacturers,
    programs,
    promotions,
    retailer,
    rewards,
  } = dashboardData ?? defaultDashboardData;

  const brandColor = getBrandColor(retailer);
  const {
    areAllCategoriesHurdled,
    currentTier,
    hasTierGraphics,
    isReachedFinalTier,
    isTierSpendHurdled,
    nextTier,
    numCategoriesToHurdle,
    relevantTier,
    spendToHurdle,
  } = useMemo(() => (
    getProgramInfo(activeProgram, activeFarmerRewards, !dashboardData)
  ), [dashboardData, activeProgram, activeFarmerRewards]);

  const hasExclusions = !!activeProgram?.categoryIdsExcludedFromTierSpend.length
    || !!activeProgram?.categoryIdsExcludedFromSegmentSpend.length;

  const progressToNextTierBannerText = useMemo(() => {
    if (!dashboardData) {
      return '';
    }
    if (numCategoriesToHurdle && spendToHurdle) {
      return t($ => $.customerDashboard.tierProgress.remainingWithSpendAndCategories, {
        spend: spendToHurdle,
        count: numCategoriesToHurdle,
      });
    } else if (numCategoriesToHurdle) {
      return t($ => $.customerDashboard.tierProgress.remainingWithCategories, {
        count: numCategoriesToHurdle,
      });
    } else if (spendToHurdle) {
      return t($ => $.customerDashboard.tierProgress.remainingWithSpend, {
        spend: spendToHurdle,
      });
    } else {
      return t($ => $.customerDashboard.tierProgress.finalTierComplete);
    }
  }, [dashboardData, numCategoriesToHurdle, spendToHurdle, t]);

  const howToReachNextTierText = useMemo(() => {
    return retailer.isProgramCreationDisabled
      ? ''
      : buildRequirementTextForTier(t, relevantTier, isReachedFinalTier, false);
  }, [
    isReachedFinalTier,
    relevantTier,
    retailer.isProgramCreationDisabled,
    t,
  ]);

  const programStartJsDate = activeProgram?.startDate
    ? getJsDateFromString(activeProgram.startDate)
    : undefined;
  const programEndJsDate = activeProgram?.endDate
    ? getJsDateFromString(activeProgram.endDate, { endOfDay: true })
    : undefined;
  const programDateRange = (programStartJsDate && programEndJsDate)
    ? t($ => $.customerDashboard.landing.programDateRange, {
      startDate: programStartJsDate,
      endDate: programEndJsDate,
    })
    : '';

  const agronomyBalances = excludeEnergyBalances(balances);
  const pointsAvailable = calculateAvailablePoints(agronomyBalances);
  const pointsLocked = calculateLockedPoints(agronomyBalances);
  const pointsReserved = calculateReservedPoints(agronomyBalances);

  const formattedPointsAvailable = formatNumber(pointsAvailable, i18n);
  const formattedPointsLocked = formatNumber(pointsLocked, i18n);
  const formattedPointsReserved = formatNumber(pointsReserved, i18n);

  const renderWalletPointsDisplay = () => {
    return (
      <Text category="label-medium">
        {t($ => $.wallet.availablePoints, { points: pointsAvailable })}
      </Text>
    );
  };

  if (!dashboardData) {
    return (
      <Container>
        <Stack alignItems="center">
          <VSpacer size="14" />
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  const Header = <DashboardHeader retailer={retailer} />;

  const ProgramInfo = activeProgram && (
    <DashboardProgramInfo
      brandColor={brandColor}
      dateRange={programDateRange}
      name={activeProgram.name}
      onHowItWorks={() => navigateToPage(Routes.CUSTOMER_DASHBOARD_HOW_IT_WORKS, accessToken)}
    />
  );

  const FarmerGreeting = <DashboardGreeting farmer={farmer} />;

  const StatusCard = !!currentTier && !retailer.isProgramCreationDisabled && (
    <>
      <VSpacer size="5" />
      <Card
        sx={cardStyle}
        testID="dashboard-status-card"
      >
        <Stack direction="row">
          {hasTierGraphics && (
            <>
              <TierImage hasTierGraphics={hasTierGraphics} tier={currentTier} />
              <HSpacer size="3" />
            </>
          )}
          <Stack>
            <Stack alignItems="center" direction="row" gap="4px">
              <Text category="overline">
                {t($ => $.customers.table.filters.currentStatus).toUpperCase()}
              </Text>
              <InfoTooltip
                backgroundColor={theme.palette.background.paper}
                boundaryRef={containerRef}
                iconColor="#525252"
                openOnClick={true}
              >
                {t($ => $.customers.details.currentStatusTooltip)}
              </InfoTooltip>
            </Stack>
            <VSpacer size="3" />
            <Stack alignItems="center" direction="row" gap="12px">
              <Text fontSize="28px" fontWeight={500}>
                {currentTier.name}
              </Text>
              {!!farmer?.accountGroupRole && (
                <CustomerRoleTypeBadge
                  accountGroupRole={farmer.accountGroupRole}
                  testID="status-card-shared-badge"
                />
              )}
            </Stack>
          </Stack>
        </Stack>
        {!!activeFarmerRewards && !!activeProgram && (
          <ReEarnStatusBanner farmerRewards={activeFarmerRewards} program={activeProgram} />
        )}
        <VSpacer size="4" />
        <Button
          color="inherit"
          fullWidth
          onClick={() => setShowTierBenefitsModal(true)}
          sx={{
            borderColor: '#D9D9D9',
            p: '10px 16px',
            ...buttonHoverStyle,
          }}
          testID="dashboard-status-card-benefits-button"
          variant="outlined"
        >
          {t($ => $.customerDashboard.tierBenefits.title, { tierName: currentTier.name })}
        </Button>
      </Card>
    </>
  );

  const BenefitsCard = (
    <ButtonBase
      onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD_BENEFITS, accessToken)}
      sx={{ width: '100%' }}
    >
      <Card
        sx={[cardStyle, { paddingRight: '24px', width: '100%' }]}
        testID="dashboard-benefits-card"
      >
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Stack alignItems="center" direction="row">
            <Text fontSize="18px" fontWeight={400}>
              {t($ => $.customerDashboard.benefits.title)}
            </Text>
          </Stack>
          <ChevronRight sx={{ color: '#525252' }} />
        </Stack>
      </Card>
    </ButtonBase>
  );

  const WalletCard = (
    <ButtonBase
      onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD_WALLET, accessToken)}
      sx={{ width: '100%' }}
    >
      <Card
        sx={[cardStyle, { paddingRight: '24px', width: '100%' }]}
        testID="wallet-card"
      >
        <Stack direction="row" justifyContent="space-between">
          <Stack alignItems="center" direction="row">
            <Text fontSize="18px" fontWeight={400}>{t($ => $.customerDashboard.wallet.title)}</Text>
          </Stack>
          <Stack alignItems="center" direction="row" textAlign="right">
            {renderWalletPointsDisplay()}
            <ChevronRight sx={{ color: '#525252' }} />
          </Stack>
        </Stack>
      </Card>
    </ButtonBase>
  );

  const renderKPICards = () => {
    const kpiCardCount = 1 + (retailer.hasBookings ? 1 : 0) + (pointsLocked ? 1 : 0);
    const availableCardWrapperStyle = kpiCardCount > 2
      ? primaryKpiCardWrapperStyle
      : kpiCardWrapperStyle;
    return (
      <>
        <VSpacer size="5" />
        <Stack direction="row" flexWrap="wrap" gap="16px" justifyContent="space-between">
          <Box sx={availableCardWrapperStyle}>
            <KPICard
              boundaryRef={containerRef}
              farmer={farmer}
              iconColor= 'rgba(12, 0, 0, 0.68)'
              label={t($ => $.customers.table.filters.pointsAvailable)}
              openTooltipOnClick={true}
              testID="kpi-card-points-available"
              tooltipText={retailer.hasBookings
                ? t($ => $.shared.walletPointsAvailableTooltip.text)
                : t($ => $.shared.walletPointsAvailableTooltip.textWithoutBookings)
              }
              valueText={formattedPointsAvailable}
              variant="centered"
            />
          </Box>
          {retailer.hasBookings && (
            <Box sx={kpiCardWrapperStyle}>
              <KPICard
                boundaryRef={containerRef}
                iconColor= 'rgba(0, 0, 0, 0.68)'
                label={t($ => $.customers.table.filters.pointsReserved)}
                openTooltipOnClick={true}
                testID="kpi-card-points-reserved"
                tooltipText={t($ => $.customers.details.pointsReservedTooltip)}
                valueText={formattedPointsReserved}
                variant="centered"
              />
            </Box>
          )}
          {!!pointsLocked && (
            <Box sx={kpiCardWrapperStyle}>
              <KPICard
                boundaryRef={containerRef}
                iconColor='rgba(0, 0, 0, 0.68)'
                label={t($ => $.customerDashboard.wallet.lockedPoints)}
                openTooltipOnClick={true}
                testID="kpi-card-points-locked"
                tooltipText={t($ => $.wallet.locked.explanation)}
                valueText={formattedPointsLocked}
                variant="centered"
              />
            </Box>
          )}
        </Stack>
      </>
    );
  };

  const StatusSection = (
    <Stack p="16px" sx={darkenedSectionStyle}>
      {FarmerGreeting}
      {farmer?.accountGroupRole && (
        <>
          <VSpacer size="3" />
          <Stack alignItems="center" direction="row" gap="8px">
            <CustomerRoleBadge
                accountGroupRole={farmer.accountGroupRole}
                testID="dashboard-customer-role-badge"
              />
            {!!farmer?.accountGroup?.externalId && (
              <Text category="overline" sx={{ color: '#525252' }}>
                {t($ => $.customers.table.filters.groupId)} {farmer?.accountGroup?.externalId}
              </Text>
            )}
          </Stack>
        </>
      )}
      {linkedAccounts.length > 0 && !retailer.isProgramCreationDisabled && (
        <>
          <VSpacer size="3" />
          <TextLink
            category="label-medium"
            onClick={() => setShowLinkedAccountsModal(true)}
            sx={{ alignItems: 'center', color: brandColor, display: 'flex', gap: '4px' }}
            testID="dashboard-linked-accounts-textlink"
          >
            <AccountTreeIcon sx={{ fontSize: '16px', marginRight: '6px' }} />
            {t($ => $.customerDashboard.linkedAccounts.title)} ({linkedAccounts.length})
          </TextLink>
        </>
      )}

      {renderKPICards()}
      {StatusCard}
      <VSpacer size="5" />
      {WalletCard}
      {!retailer.isProgramCreationDisabled && (
        <>
          <VSpacer size="5" />
          {BenefitsCard}
        </>
      )}
      <VSpacer size="4" />
    </Stack>
  );

  const TotalSpendSummary = !!activeFarmerRewards && (
    <Card
      cardContentStyle={{
        padding: '12px 16px 16px 16px',
      }}
      elevation={0}
      testID="current-spend-card"
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Text category="title-medium">
          {t($ => $.customers.table.filters.currentSpend, { amount: '' }).replace(':', '').trim()}
        </Text>
        {isTierSpendHurdled ? (
          <TextBadge text={t($ => $.customers.table.tooltips.complete)} />
        ) : (
          <Stack alignItems="center">
            <Text category="body-medium">
              {t($ => $.customerDashboard.tierProgress.remaining, {
                value: formatShortCurrency(
                  relevantTier.minimumDollars - activeFarmerRewards.qualifyingTotalSpend,
                  i18next,
                ),
              })}
            </Text>
          </Stack>
        )}
      </Stack>
      <VSpacer size="4" />
      <TotalSpendProgressBar
        minimumDollars={relevantTier.minimumDollars}
        totalSpend={activeFarmerRewards.qualifyingTotalSpend}
      />
      <VSpacer size="2" />
      <Stack direction="row" justifyContent="flex-end">
        <Text category="body-medium">
          {t($ => $.customerDashboard.tierProgress.goal, {
            value: formatShortCurrency(relevantTier.minimumDollars, i18next),
          })}
        </Text>
      </Stack>
      {!isTierSpendHurdled && (
        <>
          <VSpacer size="4" />
          <Text category="body-xsmall" sx={{ color: '#525252' }}>
            {t($ => $.customerDashboard.tierProgress.spendQualificationTooltip)}
          </Text>
        </>
      )}
    </Card>
  );

  const CategoriesHurdledChips = () => {
    const numSegments = relevantTier.minimumSegments;
    let chipWidth: string;
    if (numSegments <= 2) {
      chipWidth = '164px';
    } else if (numSegments === 4) {
      chipWidth = '77px';
    } else {
      chipWidth = '106px';
    }
    const hurdled = new Array(relevantTier.minimumSegments - numCategoriesToHurdle).fill(true);
    const notHurdled = new Array(numCategoriesToHurdle).fill(false);

    return (
      <Stack direction="row" flexWrap="wrap" gap="16px" justifyContent="center">
        {[...hurdled, ...notHurdled].map((isHurdled, i) => (
          <Stack
            alignItems="center"
            bgcolor={isHurdled ? '#237804' : theme.palette.grey[300]}
            borderRadius="4px"
            flexGrow={1}
            justifyContent="center"
            key={i}
            maxWidth={chipWidth}
            minWidth="77px"
            p="6px"
            width={chipWidth}
          >
            <Check sx={{ color: 'white' }}/>
          </Stack>
        ))}
      </Stack>
    );
  };

  const CategoriesHurdledSummary = (
    <Card
      cardContentStyle={{
        padding: '12px 16px 16px 16px',
      }}
      elevation={0}
      testID="current-spend-card"
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Text category="title-medium">
          {t($ => $.customers.table.filters.categoriesHurdled)}
        </Text>
        {areAllCategoriesHurdled && (
          <>
            <HSpacer size="3" />
            <TextBadge text={t($ => $.customers.table.tooltips.complete)} />
          </>
        )}
      </Stack>
      <VSpacer size="5" />
      <CategoriesHurdledChips/>
    </Card>
  );

  const ProgressSummary = (
    <Stack>
      {!!relevantTier.minimumDollars && TotalSpendSummary}
      {(!!relevantTier.minimumDollars && !!relevantTier.minimumSegments) && (
        <VSpacer size="4" />
      )}
      {!!relevantTier.minimumSegments && CategoriesHurdledSummary}
    </Stack>
  );

  const TierGoal = !retailer.isProgramCreationDisabled && (
    <Card
      cardContentStyle={{
        padding: '16px 16px 12px 16px',
        '&:last-child': { paddingBottom: '12px' },
      }}
      elevation={0}
      testID="tier-goal-card"
    >
      <Text category="overline">
        {isReachedFinalTier
          ? t(
            $ => $.customerDashboard.tierProgress.tierGoalMaintain,
            { tierName: currentTier?.name || '' },
          )
          : t(
            $ => $.customerDashboard.tierProgress.tierGoalReach,
            { tierName: nextTier?.name || '' },
          )
        }
      </Text>
      <VSpacer size="1" />
      <Text category="body-large">
        {howToReachNextTierText}
      </Text>
    </Card>
  );

  const ViewButton = ({ onClick, testID }: {
    onClick: () => void,
    testID: string,
  }) => <DashboardViewButton brandColor={brandColor} onClick={onClick} testID={testID} />;

  const ProgressSection = !!activeFarmerRewards && !!activeProgram && !!categories && (
    <Stack p="24px 16px 0px 16px">
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Stack alignItems="center" direction="row" gap="4px">
          <Text category="headline-medium">
            {t($ => $.customers.progress.title)}
            <HSpacer size="2" />
            <InfoTooltip
              backgroundColor={theme.palette.background.paper}
              boundaryRef={containerRef}
              iconColor="#525252"
              openOnClick={true}
            >
              {t($ => $.customerDashboard.benefits.progress, {
                startYear: programStartJsDate ? programStartJsDate.getFullYear() : '',
                endYear: programEndJsDate ? programEndJsDate.getFullYear() : '',
              })}
            </InfoTooltip>
          </Text>
        </Stack>
        <ViewButton
          onClick={() => setShowMyProgressModal(true)}
          testID="dashboard-my-progress-button"
        />
      </Stack>
      <VSpacer size="3" />
      <Text category="body-medium" sx={{ color: theme.palette.grey[800] }}>
        {buildProgramProgressText(activeFarmerRewards, activeProgram, t)}
      </Text>
      {activeFarmerRewards.rankEarned !== null && (
        <>
          <VSpacer size="5" />
          <Stack alignItems="center">
            <TierProgressBar
              rankCurrent={activeFarmerRewards.rankEarned}
              tierNames={activeProgram.tiers.map(({ name }) => name)}
            />
          </Stack>
          <VSpacer size="5" />
          <Stack alignItems="center">
            <Stack
              alignItems="center"
              bgcolor={isReachedFinalTier ? undefined : alpha(theme.palette.success.main, .12)}
              borderRadius="12px"
              direction="row"
              maxWidth="fit-content"
              p="4px 12px"
            >
              {isReachedFinalTier && (
                <>
                  <img src={PartyPopperIcon} style={{ height: '18px', width: '18px' }}/>
                  <HSpacer size="3"/>
                </>
              )}
              <Text category={isReachedFinalTier ? 'label-large' : 'body-small'}>
                {progressToNextTierBannerText}
              </Text>
            </Stack>
          </Stack>
        </>
      )}
      <VSpacer size="5" />
      {TierGoal}
      <VSpacer size="4" />
      {ProgressSummary}
      {hasExclusions && (
        <>
          <VSpacer size="5" />
          <ProgramExclusionsDisclaimer
            brandColor={brandColor}
            categories={categories}
            segmentSpendCategoryExclusionIds={activeProgram.categoryIdsExcludedFromSegmentSpend}
            tierSpendCategoryExclusionIds={activeProgram.categoryIdsExcludedFromTierSpend}
          />
        </>
      )}
    </Stack>
  );

  const PointsActivitySection = (
    <Stack p="16px">
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Stack>
          <Text category="headline-medium">
            {t($ => $.customerDashboard.pointsActivity.title)}
          </Text>
        </Stack>
        <ViewButton
          onClick={() => {
            if (accessToken) {
              navigateToPage(Routes.CUSTOMER_DASHBOARD_POINTS_ACTIVITY, accessToken);
            } else {
              openSnackbar(t($ => $.customerDashboard.errors.pointsActivityMissingToken));
            }
          }}
          testID="dashboard-points-activity-button"
        />
      </Stack>
    </Stack>
  );

  const BookingsSection = (
    <Stack p="16px">
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Stack>
          <Text category="headline-medium">
            {t($ => $.bookings.title)}
          </Text>
        </Stack>
        <ViewButton
          onClick={() => {
            if (accessToken) {
              navigateToPage(Routes.CUSTOMER_DASHBOARD_BOOKINGS, accessToken);
            } else {
              openSnackbar(t($ => $.customerDashboard.errors.bookingsMissingToken));
            }
          }} testID="dashboard-bookings-button"/>
      </Stack>
    </Stack>
  );

  const PromotionSection = (
    <Stack>
      <Stack p="16px">
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Stack>
            <Text category="headline-medium">
              {t($ => $.promotions.title)}
            </Text>
          </Stack>
          <ViewButton
            onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD_PROMOTIONS, accessToken)}
            testID="view-promotions-button"
          />
        </Stack>
      </Stack>
      <Box bgcolor={brandColor} minHeight="302px" overflow="auto" px="16px" py="24px">
        <Stack direction="row" height="100%">
          <Stack direction="row" gap="16px" height="100%">
            {sortBy(
              promotions.filter((promotion) => promotion.showInDashboardCarousel),
              ({ endDate }) => getJsDateFromString(endDate, { endOfDay: true }).getTime(),
            ).map((promotion) => (
              <PromotionCard
                key={promotion.id}
                onClickDetails={() => {
                  setSelectedPromotion(promotion);
                  setShowPromotionDetailsModal(true);
                }}
                promotion={promotion}
              />
            ))}
          </Stack>
          <div style={{ width: 16, height: 302, flexShrink: 0 }} />
        </Stack>
      </Box>
    </Stack>
  );

  const Disclaimer = <DashboardDisclaimer />;

  const Footer = <DashboardFooter brandColor={brandColor} />;

  return  (
    <>
      <Stack
        bgcolor="white"
        maxWidth="599px"
        minHeight="100vh"
        minWidth="360px"
        ref={containerRef}
      >
        {Header}
        {!!(activeProgram && accessToken) && (
          <>
            {!!programs?.length && (
              <ProgramPointsExpirationModal
                accessToken={accessToken}
                customer={dashboardData.farmer}
                programs={programs}
                retailer={retailer}
              />
            )}
            <ProgramExpirationBanner accessToken={accessToken} program={activeProgram}/>
            <Divider/>
            <PointsExpirationBanner
              accessToken={accessToken}
              customer={dashboardData.farmer}
              onNavigateToWallet={
                () => navigateToPage(Routes.CUSTOMER_DASHBOARD_WALLET, accessToken)
              }
            />
          </>
        )}
        {!retailer.isProgramCreationDisabled && ProgramInfo}
        <ProgramSwitch memberships={memberships} value="agronomy" />
        <Divider />
        {!!farmer?.accountGroupRole && !!linkedAccounts?.length && (
          <AboutAccountRolesBanner
            brandColor={brandColor}
            customerRole={farmer.accountGroupRole}
            linkedAccounts={linkedAccounts}
            pageName={CIBPageName.CRD_LandingPage}
            showMobileVersion={true}
          />
        )}
        {StatusSection}
        {!retailer.isProgramCreationDisabled && (
          <>
            <Divider />
            {ProgressSection}
          </>
        )}
        <VSpacer size="8" />
        {retailer.hasBookings && (
          <>
            <Divider />
            {BookingsSection}
            <Divider />
          </>
        )}
        {PointsActivitySection}
        {!!promotions.length && (
          <>
            <Divider />
            {PromotionSection}
          </>
        )}
        {!promotions.length && <Divider />}
        {Disclaimer}
        <VSpacer size="3" />
        <Divider />
        {Footer}
      </Stack>
      {showPromotionDetailsModal && !!selectedPromotion && accessToken && (
        <PromotionDetailsModal
          accessToken={accessToken}
          brandColor={brandColor}
          onClose={() => {
            setShowPromotionDetailsModal(false);
            setSelectedPromotion(null);
          }}
          promotionId={selectedPromotion.id}
          retailerName={retailer.name}
        />
      )}
      {showTierBenefitsModal && !!currentTier && !!categories && !!activeProgram && (
        <TierBenefitsModal
          brandColor={brandColor}
          categories={categories}
          hasTierGraphics={hasTierGraphics}
          manufacturers={manufacturers}
          onClose={() => setShowTierBenefitsModal(false)}
          program={activeProgram}
          promotions={promotions}
          retailer={retailer}
          tier={currentTier}
        />
      )}
      {showLinkedAccountsModal
        && linkedAccounts.length > 0
        && farmer.accountGroupRole && (
        <LinkedAccountsModal
          accounts={linkedAccounts}
          brandColor={brandColor}
          farmer={farmer}
          onClose={() => setShowLinkedAccountsModal(false)}
        />
      )}
      {showMyProgressModal
        && !!categories
        && !!activeProgram
        && !!activeFarmerRewards
        && !!programs
        && !!rewards
        && (
          <MyProgressModal
            activeFarmerRewards={activeFarmerRewards}
            activeProgram={activeProgram}
            brandColor={brandColor}
            categories={categories}
            customerId={farmer.id}
            farmer={farmer}
            isReachedFinalTier={isReachedFinalTier}
            linkedAccounts={linkedAccounts}
            nextTier={relevantTier}
            onClose={() => setShowMyProgressModal(false)}
            programs={programs}
            rewards={rewards}
          />
        )
      }
      {activeProgram && <NewProgramWelcomeModal program={activeProgram} />}
    </>
  );
};
